import React, { useState, useEffect } from 'react';
import chemicalService from '../../../services/chemicalService';

const blankItem = () => ({ product_id: '', ingredient_name: '', quantity: '', unit: 'Kg', rate: '' });

const CostingFormModal = ({ costing, onClose, onSaved }) => {
  const isEdit = Boolean(costing);
  const [minimized, setMinimized] = useState(false);

  const [formulas, setFormulas] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    code: '',
    formula_id: '',
    product_id: '',
    multiplier: '1',
    yield_qty: '',
    overhead_cost: '',
    commission_percent: '',
    commission_per_kg: '',
    margin_percent: '',
    notes: '',
    items: [blankItem()],
  });

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [fRes, pRes] = await Promise.all([
          chemicalService.getFormulas({ page: 1, limit: 1000 }),
          chemicalService.getProducts({ page: 1, limit: 1000 }),
        ]);
        setFormulas(fRes.data || []);
        setProducts(pRes.data || []);
      } catch (err) {
        console.error('Error loading data:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (costing) {
      setForm({
        code: costing.code || '',
        formula_id: costing.formula_id ? String(costing.formula_id) : '',
        product_id: costing.product_id ? String(costing.product_id) : '',
        multiplier: costing.multiplier != null ? String(costing.multiplier) : '1',
        yield_qty: costing.yield_qty != null ? String(costing.yield_qty) : '',
        overhead_cost: costing.overhead_cost != null && parseFloat(costing.overhead_cost) > 0 ? String(costing.overhead_cost) : '',
        commission_percent: costing.commission_percent != null ? String(costing.commission_percent) : '',
        commission_per_kg: costing.commission_per_kg != null ? String(costing.commission_per_kg) : '',
        margin_percent: costing.margin_percent != null ? String(costing.margin_percent) : '',
        notes: costing.notes || '',
        items: (costing.items && costing.items.length > 0)
          ? costing.items.map((it) => ({
              product_id: it.product_id ? String(it.product_id) : '',
              ingredient_name: it.ingredient_name || '',
              quantity: it.quantity != null ? String(it.quantity) : '',
              unit: it.unit || 'Kg',
              rate: it.rate != null ? String(it.rate) : '',
            }))
          : [blankItem()],
      });
    }
  }, [costing]);

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  // When a recipe is picked, load its ingredients × multiplier
  const handleFormulaChange = async (fid) => {
    handleChange('formula_id', fid);
    if (!fid) return;
    try {
      const res = await chemicalService.getFormulaById(fid);
      const f = res.data;
      const mult = parseFloat(form.multiplier) || 1;
      const items = (f.ingredients || []).map((ing) => ({
        product_id: ing.product_id ? String(ing.product_id) : '',
        ingredient_name: ing.product_name || '',
        quantity: ing.quantity != null ? String((parseFloat(ing.quantity) * mult)) : '',
        unit: ing.unit || 'Kg',
        rate: '',
      }));
      setForm((prev) => ({
        ...prev,
        formula_id: fid,
        product_id: f.product_id ? String(f.product_id) : '',
        yield_qty: f.yield_qty != null ? String(parseFloat(f.yield_qty) * mult) : '',
        items: items.length ? items : [blankItem()],
      }));
    } catch (err) {
      console.error('Error loading formula:', err);
    }
  };

  // Recalculate ingredient quantities + yield when multiplier changes
  const handleMultiplierChange = (val) => {
    const mult = parseFloat(val) || 0;
    setForm((prev) => {
      // scale from the recipe base: since items may be manually edited, we scale relative to current multiplier
      const prevMult = parseFloat(prev.multiplier) || 1;
      const factor = prevMult > 0 ? mult / prevMult : 1;
      return {
        ...prev,
        multiplier: val,
        yield_qty: prev.yield_qty ? String(parseFloat(prev.yield_qty) * factor) : prev.yield_qty,
        items: prev.items.map((it) => ({
          ...it,
          quantity: it.quantity ? String(parseFloat(it.quantity) * factor) : it.quantity,
        })),
      };
    });
  };

  const handleItemChange = (index, field, value) => {
    setForm((prev) => {
      const items = [...prev.items];
      items[index] = { ...items[index], [field]: value };
      if (field === 'product_id' && value) {
        const p = products.find((x) => String(x.id) === String(value));
        if (p) { items[index].ingredient_name = p.name; if (p.base_unit) items[index].unit = p.base_unit; }
      }
      return { ...prev, items };
    });
  };

  const addItem = () => setForm((prev) => ({ ...prev, items: [...prev.items, blankItem()] }));
  const removeItem = (i) => {
    if (form.items.length === 1) return alert('❌ At least one ingredient required.');
    setForm((prev) => ({ ...prev, items: prev.items.filter((_, idx) => idx !== i) }));
  };

  // ─── Live calculations ───
  const lineAmount = (it) => (parseFloat(it.quantity) || 0) * (parseFloat(it.rate) || 0);
  const rawMaterialCost = form.items.reduce((s, it) => s + lineAmount(it), 0);
  const overhead = parseFloat(form.overhead_cost) || 0;
  const yieldQty = parseFloat(form.yield_qty) || 0;

  // Commission: % is off raw+overhead, per-kg is × yield
  const commissionPct = parseFloat(form.commission_percent) || 0;
  const commissionPerKg = parseFloat(form.commission_per_kg) || 0;
  const commissionAmount = (commissionPct > 0 ? ((rawMaterialCost + overhead) * commissionPct) / 100 : 0)
                         + (commissionPerKg > 0 ? commissionPerKg * yieldQty : 0);

  const totalCost = rawMaterialCost + overhead + commissionAmount;
  const costPerKg = yieldQty > 0 ? totalCost / yieldQty : 0;

  const marginPct = parseFloat(form.margin_percent) || 0;
  const sellingPricePerKg = marginPct > 0 ? costPerKg * (1 + marginPct / 100) : costPerKg;

  const fmtMoney = (n) => 'Rs ' + (Math.round((n || 0) * 100) / 100).toLocaleString('en-PK');
  const fmtNum = (n) => (Math.round((n || 0) * 1000) / 1000).toLocaleString('en-PK');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.product_id && !form.formula_id) return alert('❌ Pick a recipe or a product.');
    if (yieldQty <= 0) return alert('❌ Estimated output (yield) must be > 0.');
    for (let i = 0; i < form.items.length; i++) {
      const it = form.items[i];
      if (!it.ingredient_name && !it.product_id) return alert(`❌ Ingredient ${i + 1}: name or product required.`);
      if (!it.quantity || parseFloat(it.quantity) <= 0) return alert(`❌ Ingredient ${i + 1}: quantity must be > 0.`);
      if (it.rate === '' || parseFloat(it.rate) < 0) return alert(`❌ Ingredient ${i + 1}: enter a rate.`);
    }

    try {
      setSubmitting(true);
      const payload = {
        code: form.code.trim() || null,
        formula_id: form.formula_id ? parseInt(form.formula_id, 10) : null,
        product_id: form.product_id ? parseInt(form.product_id, 10) : null,
        multiplier: parseFloat(form.multiplier) || 1,
        yield_qty: yieldQty,
        raw_material_cost: Math.round(rawMaterialCost * 100) / 100,
        overhead_cost: Math.round(overhead * 100) / 100,
        commission_percent: form.commission_percent ? parseFloat(form.commission_percent) : null,
        commission_per_kg: form.commission_per_kg ? parseFloat(form.commission_per_kg) : null,
        commission_amount: Math.round(commissionAmount * 100) / 100,
        total_cost: Math.round(totalCost * 100) / 100,
        cost_per_kg: Math.round(costPerKg * 1000) / 1000,
        margin_percent: form.margin_percent ? parseFloat(form.margin_percent) : null,
        selling_price_per_kg: Math.round(sellingPricePerKg * 1000) / 1000,
        notes: form.notes.trim() || null,
        items: form.items.map((it, i) => ({
          product_id: it.product_id ? parseInt(it.product_id, 10) : null,
          ingredient_name: it.ingredient_name.trim() || null,
          quantity: parseFloat(it.quantity) || 0,
          unit: it.unit,
          rate: parseFloat(it.rate) || 0,
          amount: Math.round(lineAmount(it) * 100) / 100,
          display_order: i,
        })),
      };
      if (isEdit) {
        await chemicalService.updateCosting(costing.id, payload);
        alert('✅ Costing updated!');
      } else {
        await chemicalService.createCosting(payload);
        alert('✅ Costing saved as draft!');
      }
      onSaved();
    } catch (err) {
      alert('❌ ' + (err.response?.data?.message || err.message || 'Failed to save costing'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={minimized ? styles.minimizedBar : styles.modalOverlay}>
      <div
        style={minimized ? styles.minimizedModal : styles.modal}
        onClick={minimized ? () => setMinimized(false) : (e) => e.stopPropagation()}
      >
        {minimized ? (
          <div style={styles.minimizedContent}>
            <span style={{ fontWeight: 700, color: '#2c3e50' }}>🧮 {isEdit ? 'Edit' : 'New'} Costing</span>
            <span style={{ fontSize: 12, color: '#7f8c8d' }}>Click to restore</span>
            <button onClick={(e) => { e.stopPropagation(); onClose(); }} style={styles.closeButton}>✕</button>
          </div>
        ) : (
          <>
            <div style={styles.modalHeader}>
              <div>
                <h2 style={styles.modalTitle}>🧮 {isEdit ? 'Edit Costing' : 'New Costing'}</h2>
                <p style={{ color: '#7f8c8d', fontSize: '14px', margin: '4px 0 0 0' }}>
                  Estimate production cost before manufacturing — for quotations
                </p>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => setMinimized(true)} style={styles.minimizeButton}>➖</button>
                <button onClick={onClose} style={styles.closeButton}>✕</button>
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              <div style={{ padding: '24px 30px' }}>

                <div style={styles.sectionLabel}>Basis</div>
                <div style={styles.row}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Costing Code</label>
                    <input type="text" value={form.code} onChange={(e) => handleChange('code', e.target.value)} style={styles.input} placeholder="e.g. Q-001 (optional)" />
                  </div>
                  <div style={{ ...styles.formGroup, flex: 2 }}>
                    <label style={styles.label}>From Recipe (optional)</label>
                    <select value={form.formula_id} onChange={(e) => handleFormulaChange(e.target.value)} style={styles.input} disabled={loading}>
                      <option value="">-- None (manual entry) --</option>
                      {formulas.map((f) => (<option key={f.id} value={f.id}>{f.code} — {f.product_name}</option>))}
                    </select>
                    <span style={styles.hint}>Picking a recipe auto-fills ingredients.</span>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Multiplier</label>
                    <input type="number" step="0.001" min="0" value={form.multiplier} onChange={(e) => handleMultiplierChange(e.target.value)} style={styles.input} placeholder="1" />
                  </div>
                </div>

                <div style={styles.row}>
                  <div style={{ ...styles.formGroup, flex: 2 }}>
                    <label style={styles.label}>Output Product</label>
                    <select value={form.product_id} onChange={(e) => handleChange('product_id', e.target.value)} style={styles.input} disabled={loading}>
                      <option value="">-- Select product --</option>
                      {products.map((p) => (<option key={p.id} value={p.id}>{p.code} — {p.name}</option>))}
                    </select>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Estimated Output (Kg) <span style={styles.required}>*</span></label>
                    <input type="number" step="0.001" min="0" value={form.yield_qty} onChange={(e) => handleChange('yield_qty', e.target.value)} style={styles.input} placeholder="0" required />
                  </div>
                </div>

                {/* Ingredients */}
                <div style={styles.sectionLabel}>Ingredients & Rates</div>
                <div style={styles.itemsList}>
                  {form.items.map((item, index) => (
                    <div key={index} style={styles.itemCard}>
                      <div style={styles.itemHeader}>
                        <div style={styles.itemIndex}>{index + 1}</div>
                        <span style={styles.lineAmount}>{fmtMoney(lineAmount(item))}</span>
                        <button type="button" onClick={() => removeItem(index)} style={styles.removeButton} disabled={form.items.length === 1}>🗑️</button>
                      </div>
                      <div style={styles.itemRow}>
                        <div style={{ flex: 3, minWidth: '180px' }}>
                          <label style={styles.miniLabel}>Ingredient</label>
                          <select value={item.product_id} onChange={(e) => handleItemChange(index, 'product_id', e.target.value)} style={styles.input}>
                            <option value="">-- Select / manual below --</option>
                            {products.map((p) => (<option key={p.id} value={p.id}>{p.code} — {p.name}</option>))}
                          </select>
                          {!item.product_id && (
                            <input type="text" value={item.ingredient_name} onChange={(e) => handleItemChange(index, 'ingredient_name', e.target.value)} style={{ ...styles.input, marginTop: 6 }} placeholder="or type name" />
                          )}
                        </div>
                        <div style={{ flex: 1, minWidth: '90px' }}>
                          <label style={styles.miniLabel}>Qty</label>
                          <input type="number" step="0.001" min="0" value={item.quantity} onChange={(e) => handleItemChange(index, 'quantity', e.target.value)} style={styles.input} required />
                        </div>
                        <div style={{ flex: 1, minWidth: '80px' }}>
                          <label style={styles.miniLabel}>Unit</label>
                          <input type="text" value={item.unit} onChange={(e) => handleItemChange(index, 'unit', e.target.value)} style={styles.input} />
                        </div>
                        <div style={{ flex: 1, minWidth: '100px' }}>
                          <label style={styles.miniLabel}>Rate/Unit</label>
                          <input type="number" step="0.001" min="0" value={item.rate} onChange={(e) => handleItemChange(index, 'rate', e.target.value)} style={styles.input} placeholder="0" required />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <button type="button" onClick={addItem} style={styles.addItemButton}>+ Add ingredient</button>

                {/* Costs */}
                <div style={styles.sectionLabel}>Additional Costs</div>
                <div style={styles.row}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Overhead Cost (optional)</label>
                    <input type="number" step="0.01" min="0" value={form.overhead_cost} onChange={(e) => handleChange('overhead_cost', e.target.value)} style={styles.input} placeholder="labor, electricity, etc." />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Commission %</label>
                    <input type="number" step="0.001" min="0" value={form.commission_percent} onChange={(e) => handleChange('commission_percent', e.target.value)} style={styles.input} placeholder="optional" />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Commission / Kg</label>
                    <input type="number" step="0.001" min="0" value={form.commission_per_kg} onChange={(e) => handleChange('commission_per_kg', e.target.value)} style={styles.input} placeholder="optional" />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Profit Margin %</label>
                    <input type="number" step="0.001" min="0" value={form.margin_percent} onChange={(e) => handleChange('margin_percent', e.target.value)} style={styles.input} placeholder="e.g. 20" />
                  </div>
                </div>

                {/* Results */}
                <div style={styles.resultsBox}>
                  <div style={styles.resultRow}><span>Raw Material Cost</span><span>{fmtMoney(rawMaterialCost)}</span></div>
                  {overhead > 0 && <div style={styles.resultRow}><span>Overhead</span><span>{fmtMoney(overhead)}</span></div>}
                  {commissionAmount > 0 && <div style={styles.resultRow}><span>Commission</span><span>{fmtMoney(commissionAmount)}</span></div>}
                  <div style={styles.resultRowTotal}><span>Total Cost</span><span>{fmtMoney(totalCost)}</span></div>
                  <div style={styles.resultRowKg}><span>Cost / Kg</span><span>{fmtMoney(costPerKg)}</span></div>
                  {marginPct > 0 && (
                    <div style={styles.resultRowSelling}><span>Suggested Selling / Kg ({marginPct}% margin)</span><span>{fmtMoney(sellingPricePerKg)}</span></div>
                  )}
                  <div style={{ fontSize: 11, color: '#95a5a6', marginTop: 6 }}>Based on estimated output of {fmtNum(yieldQty)} Kg</div>
                </div>

                <div style={{ ...styles.formGroup, marginTop: 16 }}>
                  <label style={styles.label}>Notes (optional)</label>
                  <textarea value={form.notes} onChange={(e) => handleChange('notes', e.target.value)} style={{ ...styles.input, minHeight: '50px', resize: 'vertical', fontFamily: 'inherit' }} rows="2" placeholder="Quotation notes..." />
                </div>

              </div>

              <div style={styles.modalFooter}>
                <button type="button" onClick={onClose} style={styles.cancelButton} disabled={submitting}>Cancel</button>
                <button type="submit" style={styles.submitButton} disabled={submitting}>
                  {submitting ? '⏳ Saving...' : (isEdit ? '💾 Update Costing' : '💾 Save Draft')}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

const styles = {
  minimizeButton: { padding: '8px 14px', background: '#f0f0f0', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', fontWeight: '700', color: '#666' },
  minimizedBar: { position: 'fixed', bottom: 0, left: 0, right: 0, display: 'flex', justifyContent: 'flex-end', padding: '0 20px 12px', zIndex: 1000, pointerEvents: 'none' },
  minimizedModal: { background: 'white', borderRadius: '10px 10px 0 0', boxShadow: '0 -4px 20px rgba(0,0,0,0.15)', padding: '12px 20px', cursor: 'pointer', pointerEvents: 'all', minWidth: '260px' },
  minimizedContent: { display: 'flex', alignItems: 'center', gap: '12px' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
  modal: { background: 'white', borderRadius: '12px', width: '95%', maxWidth: '960px', maxHeight: '92vh', overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' },
  modalHeader: { padding: '20px 30px', borderBottom: '2px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'sticky', top: 0, background: 'white', zIndex: 10 },
  modalTitle: { fontSize: '22px', fontWeight: '700', color: '#2c3e50', margin: 0 },
  closeButton: { padding: '8px 16px', background: '#f0f0f0', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '20px', fontWeight: '700', color: '#666' },
  sectionLabel: { fontSize: '12px', fontWeight: '700', color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '18px', marginBottom: '12px', paddingBottom: '6px', borderBottom: '1px solid #f0f0f0' },
  row: { display: 'flex', gap: '16px', flexWrap: 'wrap' },
  formGroup: { flex: 1, minWidth: '160px', display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '12px' },
  label: { fontSize: '13px', fontWeight: '600', color: '#2c3e50' },
  miniLabel: { fontSize: '11px', fontWeight: '600', color: '#7f8c8d', marginBottom: '4px', display: 'block', textTransform: 'uppercase' },
  required: { color: '#e74c3c' },
  input: { width: '100%', padding: '10px 12px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', fontFamily: 'inherit' },
  hint: { fontSize: '11px', color: '#95a5a6', fontStyle: 'italic' },
  itemsList: { display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '12px' },
  itemCard: { background: '#f8f9fa', borderRadius: '10px', padding: '14px', border: '1px solid #e0e0e0' },
  itemHeader: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' },
  itemIndex: { width: '24px', height: '24px', borderRadius: '50%', background: '#8e44ad', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700', flexShrink: 0 },
  lineAmount: { fontSize: '14px', fontWeight: '700', color: '#2c3e50', flex: 1 },
  removeButton: { padding: '6px 12px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' },
  itemRow: { display: 'flex', gap: '10px', flexWrap: 'wrap' },
  addItemButton: { width: '100%', padding: '12px', background: 'white', color: '#8e44ad', border: '2px dashed #8e44ad', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '700', marginBottom: '12px' },
  resultsBox: { background: '#f4f0fa', borderRadius: '10px', padding: '16px 20px', marginTop: '8px' },
  resultRow: { display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#7f8c8d', padding: '4px 0' },
  resultRowTotal: { display: 'flex', justifyContent: 'space-between', fontSize: '16px', fontWeight: '700', color: '#2c3e50', padding: '8px 0 4px', borderTop: '1px solid #d5c8e8', marginTop: '4px' },
  resultRowKg: { display: 'flex', justifyContent: 'space-between', fontSize: '16px', fontWeight: '700', color: '#8e44ad', padding: '4px 0' },
  resultRowSelling: { display: 'flex', justifyContent: 'space-between', fontSize: '16px', fontWeight: '700', color: '#27ae60', padding: '6px 0', borderTop: '1px dashed #d5c8e8', marginTop: '4px' },
  modalFooter: { padding: '16px 30px', borderTop: '2px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end', gap: '12px', position: 'sticky', bottom: 0, background: 'white' },
  cancelButton: { padding: '12px 24px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  submitButton: { padding: '12px 28px', background: '#8e44ad', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
};

export default CostingFormModal;